import { captureError } from '@/lib/monitoring/sentry';

interface ErrorContext {
  operation?: string;
  entityId?: string;
  metadata?: Record<string, unknown>;
  userId?: string;
}

interface ErrorLoggerConfig {
  environment: string;
  enableRemoteLogging: boolean;
  appVersion?: string;
}

const appVersion =
  typeof import.meta.env['VITE_APP_VERSION'] === 'string'
    ? import.meta.env['VITE_APP_VERSION']
    : undefined;
const config: ErrorLoggerConfig = {
  environment: import.meta.env.MODE || 'development',
  enableRemoteLogging: import.meta.env.PROD || false,
  ...(appVersion ? { appVersion } : {}),
};

export function logError(error: unknown, context?: ErrorContext): void {
  const errorMessage = getErrorDetails(error);
  const logData = {
    error: errorMessage,
    context,
    timestamp: new Date().toISOString(),
    environment: config.environment,
    appVersion: config.appVersion,
  };

  if (config.environment === 'development') {
    console.error('[Error Logger]', logData);
  } else {
    console.error('[Error Logger]', errorMessage.message, context);
  }

  if (config.enableRemoteLogging) {
    sendToMonitoringService(error, context, logData);
  }
}

function getErrorDetails(error: unknown): {
  message: string;
  name?: string;
  stack?: string;
  raw: unknown;
} {
  if (error instanceof Error) {
    return {
      message: error.message,
      ...(error.name ? { name: error.name } : {}),
      ...(error.stack ? { stack: error.stack } : {}),
      raw: error,
    };
  }

  if (typeof error === 'string') {
    return {
      message: error,
      raw: error,
    };
  }

  if (error && typeof error === 'object') {
    if ('message' in error && typeof error.message === 'string') {
      const errorName = 'name' in error && typeof error.name === 'string' ? error.name : null;
      return {
        message: error.message,
        ...(errorName ? { name: errorName } : {}),
        raw: error,
      };
    }
  }

  return {
    message: 'Unknown error occurred',
    raw: error,
  };
}

function sendToMonitoringService(
  error: unknown,
  context: ErrorContext | undefined,
  logData: Record<string, unknown>
): void {
  if (error instanceof Error && context !== undefined) {
    const sentryContext: Record<string, unknown> = {
      operation: context.operation,
      entityId: context.entityId,
      userId: context.userId,
      ...(context.metadata !== undefined ? { metadata: context.metadata } : {}),
    };
    captureError(error, sentryContext);
  } else if (error instanceof Error) {
    captureError(error);
  }

  try {
    const existingErrors = sessionStorage.getItem('error_logs');
    const errors: unknown = existingErrors ? JSON.parse(existingErrors) : [];
    const errorArray = Array.isArray(errors) ? errors : [];
    errorArray.push(logData);
    const MAX_LOGGED_ERRORS = 50;
    sessionStorage.setItem('error_logs', JSON.stringify(errorArray.slice(-MAX_LOGGED_ERRORS)));
  } catch {
    return;
  }
}

export function getLoggedErrors(): unknown[] {
  try {
    const errors = sessionStorage.getItem('error_logs');
    const parsed: unknown = errors ? JSON.parse(errors) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function clearLoggedErrors(): void {
  try {
    sessionStorage.removeItem('error_logs');
  } catch {
    return;
  }
}
